import React, { useEffect, useRef, useState } from 'react'

const sanitizeDecimal = (text: string): string =>
    text
        .replace(/,/g, '.') // coma → punto
        .replace(/[^0-9.]/g, '') // solo dígitos y punto
        .replace(/(\..*)\./g, '$1') // un solo punto permitido

import { Text, TouchableOpacity, View, TextInput } from 'react-native'
import {
    FormContentsModel,
    FormFieldsModel,
    ModelAnswerFromProps,
    ModuleInputAnswer,
    SecondaryActionsModel,
} from '../models/StoreDetailModel'
import theme from '../../../../theme'
import styles from './ModuleShareStyles'
import { placeholderGeneral } from '../../customUtils/Constants'
import { Feather } from '@expo/vector-icons'
import { updateFormAnswerSchedule } from '../../../../db-sqlite/repositories/schedule.repository'
import { useBottomSheet } from '../../customUtils/context/BottomSheetContext'
import { ImageWithZoom } from '../../customUtils/Components/ImageWIthZoom'
import useImageLoader from '../../../hooks/useImageLoader'
import { PATH_ASSETS } from '../../../utils/Constants'
import { Loader } from '../../customUtils/Components/Loader'
import { Image } from 'expo-image'

interface ModuleShareItemProps {
    item: FormContentsModel
    hideOrShow: (
        hides?: string[],
        show?: string[],
        data?: FormContentsModel[],
    ) => void
    form_id: string
    schedule_id: string
    store_id: string
    checkComplete: (option?: ModelAnswerFromProps) => void
    data: FormContentsModel[]
    name_form?: string
    keyboardHiddenState: boolean
    onFocus: (id: string) => void
    activeInputId: string
    onSubInputMeasure?: (id: string, screenY: number, inputH: number) => void
}

type StatusType = 'PENDIENTE' | 'INCOMPLETO' | 'COMPLETO'

const StatusBadge = ({ status }: { status: StatusType }) => {
    const colorMap: Record<StatusType, string> = {
        PENDIENTE: theme.colors.mediumGray,
        INCOMPLETO: theme.colors.colorDisapproved,
        COMPLETO: theme.colors.colorApproved,
    }
    const color = colorMap[status]
    let text = () => {
        switch (status) {
            case 'PENDIENTE':
                return 'Pendiente de completar'
            case 'INCOMPLETO':
                return '* Campos requeridos'
            case 'COMPLETO':
                return 'COMPLETO'
            default:
                ''
        }
    }
    if (status != 'INCOMPLETO' || !colorMap[status]) {
        return <></>
    }
    return (
        <View style={[styles.statusBadge, { marginHorizontal: 12 }]}>
            <Text style={[styles.statusBadgeText, { color }]}>{text()}</Text>
        </View>
    )
}

/**
 * Calcula el score total del item type 47 (module_share).
 *
 * Fórmula por marca: (PH / IP) * 100 >= percentageDifference → suma action.score
 *
 * @param IP  - Input padre (total módulos categoría)
 * @param subValues - Mapa { action._id: valor ingresado }
 * @param secondaryActions - secondary_actions del item
 */
function calculateModuleShareScore(
    IP: number,
    subValues: { [key: string]: string },
    secondaryActions: SecondaryActionsModel[],
): number {
    if (isNaN(IP) || IP <= 0) return 0

    let totalScore = 0
    for (const action of secondaryActions) {
        const PH = parseFloat(subValues[action._id] ?? '')
        if (isNaN(PH)) continue
        const percentage = (PH / IP) * 100
        const threshold = action.percentageDifference ?? 0
        if (percentage >= threshold) {
            totalScore += action.score ?? 0
        }
    }
    return totalScore
}

export const ModuleShareItem = React.memo(
    ({
        item,
        hideOrShow,
        form_id,
        schedule_id,
        store_id,
        checkComplete,
        data,
        name_form,
        keyboardHiddenState,
        onFocus,
        activeInputId,
        onSubInputMeasure,
    }: ModuleShareItemProps) => {
        const [mainValue, setMainValue] = useState('')
        const [subValues, setSubValues] = useState<{ [key: string]: string }>({})
        const [expanded, setExpanded] = useState(true)
        const subInputRefs = useRef<{ [key: string]: TextInput | null }>({})

        // ── Restaurar respuesta previa ──────────────────────────────────────
        useEffect(() => {
            if (item?.old_response) {
                setMainValue(item.old_response.question_input || '')

                const restored: { [key: string]: string } = {}
                item.old_response.response_inputs_modules?.forEach(
                    (moduleAnswer) => {
                        restored[moduleAnswer.question_id] =
                            moduleAnswer.questionInput ?? ''
                    },
                )
                setSubValues(restored)
            }
        }, [item?.old_response])

        // ── Status badge ────────────────────────────────────────────────────
        const totalInputs = 1 + (item.secondary_actions?.length ?? 0)
        const filledCount =
            (mainValue.trim() !== '' ? 1 : 0) +
            Object.values(subValues).filter((v) => v.trim() !== '').length

        const status: StatusType =
            filledCount === 0
                ? 'PENDIENTE'
                : filledCount === totalInputs
                  ? 'COMPLETO'
                  : 'INCOMPLETO'

        // ── Guardado ────────────────────────────────────────────────────────
        const saveAnswer = async (
            currentMainValue: string,
            currentSubValues: { [key: string]: string },
        ) => {
            const IP = parseFloat(currentMainValue)
            const secondaryActions = item.secondary_actions ?? []
            const now = new Date().toString()

            const totalScore = calculateModuleShareScore(
                IP,
                currentSubValues,
                secondaryActions,
            )

            // Cada módulo se guarda como un objeto independiente dentro de response_inputs_modules
            const response_inputs_modules: ModuleInputAnswer[] =
                secondaryActions.map((action) => {
                    const inputValue = currentSubValues[action._id] ?? ''
                    const PH = parseFloat(inputValue)
                    const threshold = action.percentageDifference ?? 0
                    const earnedScore =
                        isFinite(IP) &&
                        IP > 0 &&
                        isFinite(PH) &&
                        (PH / IP) * 100 >= threshold
                            ? (action.score ?? 0)
                            : 0
                    return {
                        _id: action._id,
                        question_id: action._id,
                        question_text: action.title ?? '',
                        question_input: inputValue,
                        questionInput: inputValue,
                        option_selected_text: [],
                        option_selected_id: [],
                        questionOptionSelected: [],
                        questionOptionSelectedText: [],
                        questionType: {
                            title: 'input_number',
                            type: 9,
                            id: '',
                        },
                        score: earnedScore.toString(),
                        media: '',
                        field_media: '',
                        field_text: '',
                        status: 1,
                        date_finish: now,
                        date_init: now,
                        questionScore: action.score ?? 0,
                        type: 9,
                        ponderar: false,
                        question: action.title ?? '',
                        questionPrice: '',
                        description: '',
                        tag: '',
                        questionImage: '',
                    }
                })

            const answer: ModelAnswerFromProps = {
                _id: item._id,
                option_selected_text: [],
                option_selected_id: [],
                question_id: item._id,
                media: '',
                question_text: item.title,
                score: totalScore.toString(),
                status: 1,
                date_finish: now,
                date_init: now,
                question_input: currentMainValue,
                field_media: '',
                field_text: '',
                questionScore: item.questionScore,
                type: item.input_type.type,
                ponderar: item.questionScore && item.questionScore > 0,
                question: item.title,
                questionOptionSelected: [],
                questionOptionSelectedText: [],
                questionType: {
                    title: item.input_type.title,
                    type: item.input_type.type,
                    id: item.input_type._id,
                },
                questionPrice: '',
                description: item.description ?? '',
                tag: '',
                questionImage: '',
                questionInput: currentMainValue,
                response_inputs_modules,
            }

            try {
                await updateFormAnswerSchedule({
                    newAnswer: answer,
                    schedule_id,
                    form_id,
                    store_id,
                    type: 'ModuleShareItem',
                })
                await checkComplete(answer)
            } catch (error) {
                console.error('ModuleShareItem -> saveAnswer =>', error)
            }
        }

        return (
            <View style={styles.container}>
                {item.title && <Text style={styles.title}>{item.title}</Text>}

                <TextInput
                    style={styles.mainInput}
                    value={mainValue}
                    onChangeText={(text) => setMainValue(sanitizeDecimal(text))}
                    placeholder='Ingresar cantidad ...'
                    keyboardType='number-pad'
                    placeholderTextColor={theme.colors.inputPlaceholder}
                    onFocus={() => onFocus(item._id)}
                    onEndEditing={(e) =>
                        saveAnswer(sanitizeDecimal(e.nativeEvent.text), subValues)
                    }
                />

                {item.secondary_actions && item.secondary_actions.length > 0 && (
                    <>
                        <View style={styles.accordionContent}>
                            {item.secondary_actions.map((action) => (
                                <View
                                    key={action._id}
                                    style={styles.subItemContainer}
                                >
                                    <Text style={styles.subItemTitle}>
                                        {action.title}
                                    </Text>
                                    <TextInput
                                        ref={(r) => {
                                            subInputRefs.current[action._id] = r
                                        }}
                                        style={styles.subInput}
                                        value={subValues[action._id] ?? ''}
                                        onChangeText={(text) =>
                                            setSubValues((prev) => ({
                                                ...prev,
                                                [action._id]: sanitizeDecimal(text),
                                            }))
                                        }
                                        placeholder='Ingresa un número ...'
                                        keyboardType='number-pad'
                                        placeholderTextColor={
                                            theme.colors.inputPlaceholder
                                        }
                                        onFocus={() => {
                                            onFocus(action._id)
                                            if (onSubInputMeasure) {
                                                subInputRefs.current[
                                                    action._id
                                                ]?.measureInWindow((x, y, w, h) => {
                                                    onSubInputMeasure(
                                                        action._id,
                                                        y,
                                                        h,
                                                    )
                                                })
                                            }
                                        }}
                                        onEndEditing={(e) => {
                                            const cleaned = sanitizeDecimal(
                                                e.nativeEvent.text,
                                            )
                                            const updated = {
                                                ...subValues,
                                                [action._id]: cleaned,
                                            }
                                            saveAnswer(mainValue, updated)
                                        }}
                                    />
                                </View>
                            ))}
                        </View>
                        <StatusBadge status={status} />
                    </>
                )}

                {/* <View style={styles.dividerHorizontalLine} /> */}
            </View>
        )
    },
)
